import { AppNS } from 'app';
import { apiGetProjectManagers } from 'app/api/users';
import { useAuthStore } from 'app/core/store_v2/auth';
import { UserData } from 'app/types';
import axios from 'axios';

import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';

import { signOut } from '../services/jwtService';
import { AppThunk } from './';
import { AVATAR_REDUCERS } from './auth/reducers/avatar';
import { DESCRIPTION_DATA_REDUCERS } from './auth/reducers/descriptionData';
import { OFFICE_CONTACT_DATA_REDUCERS } from './auth/reducers/officeContactData';
import { ONBOARDING_STEP_REDUCERS } from './auth/reducers/onboardingStep';
import { PERSONAL_DETAILS_REDUCERS } from './auth/reducers/personalDetails';
import { SETTINGS_REDUCERS } from './auth/reducers/settings';

const cleanLocalStorage = () => {
  localStorage.removeItem('dcra_step1_success');
  localStorage.removeItem('dcra_client_register_success');
};

export interface AuthState {
  user: AppNS.User | null;
  projectManagers: Array<AppNS.User> | null;
  isLoadingProjectManagers: boolean;
  isChangingAddress: boolean;
  isChangingAddressError: boolean;
  isChangingAvatar: boolean;
  isChangingAvatarError: boolean;
  isChangingLanguages: boolean;
  isChangingLanguagesError: boolean;
  isChangingPersonalData: boolean;
  isChangingPersonalDataError: boolean;
  isChangingStep: boolean;
  isChangingStepError: boolean;
  isLoggingOut: boolean;
  isChangingDescriptionData: string;
  isChangingDescriptionDataError: boolean;
  isChangingEmail: boolean;
  isChangingEmailError: boolean;
  isChangingEmailSuccess: boolean;
  isChangingMobile: boolean;
  isChangingMobileError: boolean;
  isChangingMobileSuccess: boolean;
  isChangingPublicDetailsData: boolean;
  isChangingPublicDetailsDataError: boolean;
  isChangingOfficeContactData: boolean;
  isChangingOfficeContactDataError: boolean;
  isChangingProposalPricing: boolean;
  isChangingProposalPricingError: boolean;
  isChangingFixedPricing: boolean;
  isChangingFixedPricingError: boolean;
  currentApp: string;
}

const initialState: AuthState = {
  user: null,
  projectManagers: [],
  isLoadingProjectManagers: false,
  isChangingAddress: false,
  isChangingAddressError: false,
  isChangingAvatar: false,
  isChangingAvatarError: false,
  isChangingLanguages: false,
  isChangingLanguagesError: false,
  isChangingPersonalData: false,
  isChangingPersonalDataError: false,
  isChangingStep: false,
  isChangingStepError: false,
  isLoggingOut: false,
  isChangingDescriptionData: '',
  isChangingDescriptionDataError: false,
  isChangingEmail: false,
  isChangingEmailError: false,
  isChangingEmailSuccess: false,
  isChangingMobile: false,
  isChangingMobileError: false,
  isChangingMobileSuccess: false,
  isChangingPublicDetailsData: false,
  isChangingPublicDetailsDataError: false,
  isChangingOfficeContactData: false,
  isChangingOfficeContactDataError: false,
  isChangingProposalPricing: false,
  isChangingProposalPricingError: false,
  isChangingFixedPricing: false,
  isChangingFixedPricingError: false,
  currentApp: localStorage.getItem('currentApp') || 'third-party',
};

const getRole = (roles: AppNS.Role[]): AppNS.Role => {
  if (roles.length === 1) {
    return roles[0];
  }
  if (roles.includes('agency')) {
    return 'agency';
  } else if (roles.includes('backoffice')) {
    return 'backoffice';
  } else {
    return roles[0];
  }
};

// Connection with APP_V2 store
const connectUserStores = (user: AppNS.User) => {
  useAuthStore.setState({
    currentUser: user as unknown as UserData,
  });
};

const ADMIN_ROLES = ['inspector', 'backoffice'];

const checkIsDefaultAppForAdmin = (user: AppNS.User): boolean => {
  return user.roles.some((role) => ADMIN_ROLES.includes(role));
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    ...AVATAR_REDUCERS,
    ...ONBOARDING_STEP_REDUCERS,
    ...PERSONAL_DETAILS_REDUCERS,
    ...DESCRIPTION_DATA_REDUCERS,
    ...SETTINGS_REDUCERS,
    ...OFFICE_CONTACT_DATA_REDUCERS,
    setAuthData(state: AuthState, action: { payload: AppNS.User }) {
      state.user = action.payload;
      state.user.role = getRole(action.payload.roles);

      connectUserStores(action.payload);

      if (!localStorage.getItem('currentApp')) {
        if (checkIsDefaultAppForAdmin(state.user)) {
          state.currentApp = 'dcra-inspections';
          localStorage.setItem('currentApp', 'dcra-inspections');
        }
      }
      if (state.user.employments && state.user.employments[0]) {
        state.user.company = state.user.employments[0].company;
      }

      if (state.user.onboardingStep || state.user.roles.includes('client')) {
        cleanLocalStorage();
      }
    },
    updateUserData(state: AuthState, action: { payload: AppNS.Params }) {
      state.user = { ...state.user, ...action.payload } as AppNS.User;
    },
    updateUserCompany(state: AuthState, action: { payload: AppNS.Params }) {
      if (state.user) {
        state.user.company = {
          ...state.user.company,
          ...action.payload.model.properties,
          avatar: action.payload.model.avatar,
          companyName: action.payload.model.properties.name,
        };
      }
    },
    logoutStart(state: AuthState) {
      state.isLoggingOut = true;
    },
    logout(state: AuthState, action: { payload: boolean | undefined }) {
      cleanLocalStorage();
      state.user = null;
      signOut(action?.payload);
    },
    getProjectManagersStart(state: AuthState) {
      state.isLoadingProjectManagers = true;
    },
    getProjectManagersSuccess(state: AuthState, action: PayloadAction<Array<AppNS.User>>) {
      state.isLoadingProjectManagers = false;
      state.projectManagers = action.payload;
    },
    setCurrentApp(state: AuthState, action: { payload: string }) {
      state.currentApp = action.payload;
      localStorage.setItem('currentApp', action.payload);
    },
    setInspectorProfileId(state: AuthState, action: { payload: string }) {
      if (state.user) {
        state.user = {
          ...state.user,
          inspectorProfile: {
            id: action.payload,
          } as AppNS.InspectorProfile,
        };
        connectUserStores({ ...current(state).user } as AppNS.User);
      }
    },
    setNewEmployment(state: AuthState, action: { payload: AppNS.Employment }) {
      if (state.user) {
        state.user = {
          ...state.user,
          employments: [...(state.user.employments || []), action.payload],
        };
        connectUserStores({ ...current(state).user } as AppNS.User);
      }
    },
    setUserRoles(state: AuthState, action: { payload: AppNS.Role[] }) {
      if (state.user) {
        state.user.roles = action.payload;
        connectUserStores({ ...current(state).user } as AppNS.User);
      }
    },
  },
});

export const {
  changeOnboardingStepFailed,
  changeOnboardingStepStart,
  changeOnboardingStepSuccess,
  logout,
  logoutStart,
  setAuthData,
  setAvatarFailed,
  setAvatarStart,
  setAvatarSuccess,
  setCompanyAvatarSuccess,
  setPersonalDataFailed,
  setPersonalDataStart,
  setPersonalDataSuccess,
  updateUserData,
  updateUserCompany,
  setDescriptionDataFailed,
  setDescriptionDataStart,
  setDescriptionDataSuccess,
  changeEmailStart,
  changeEmailFailed,
  changeEmailSuccess,
  changeMobileStart,
  changeMobileFailed,
  changeMobileSuccess,
  setOfficeContactDataStart,
  setOfficeContactDataFailed,
  setOfficeContactDataSuccess,
  setProposalPricingStart,
  setProposalPricingFailed,
  setProposalPricingSuccess,
  setFixedPricingStart,
  setFixedPricingFailed,
  setFixedPricingSuccess,
  getProjectManagersStart,
  getProjectManagersSuccess,
  setCurrentApp,
  setInspectorProfileId,
  setUserRoles,
  setNewEmployment,
} = authSlice.actions;

export default authSlice.reducer;

export const getProjectManagers =
  (agencyCompanyId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getProjectManagersStart());
      const response = await apiGetProjectManagers(agencyCompanyId);

      dispatch(getProjectManagersSuccess(response));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      dispatch(getProjectManagersSuccess([]));
    }
  };
